import useToken from 'hooks/useToken';
import React, { useEffect, useState, useMemo } from 'react';
import { Card, Col, Form, Row, Spinner, Button, Modal, Table, Badge } from 'react-bootstrap';
import { useToast } from 'context/providers/ToastProvider';
import TablaLicenciasPendientes from './TablaLicenciasPendientes';
import TablaEmpresasYaProgramadas from './TablaEmpresasYaProgramadas';
import TablaEmpresasYaBorradas from './TablaEmpresasYaBorradas';
import TarjetasResumenKPI from './TarjetasResumenKPI';
import HeaderFiltrosBorrado from './HeaderFiltrosBorrado';

const LicenciasVencidasDataBorrado = () => {
    const [candidatasTotal, setCandidatasTotal] = useState([]);
    const [licenciasSeleccionadas, setLicenciasSeleccionadas] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [resumen, setResumen] = useState({});
    const [busqueda, setBusqueda] = useState('');
    const [conteoTabs, setConteoTabs] = useState({
        0: 0,
        1: 0,
        2: 0,
        3: 0
    });

    // Estado para el modal de confirmación
    const [modalConfirm, setModalConfirm] = useState({
        show: false,
        title: '',
        message: '',
        onConfirm: null,
        variant: 'danger',
        confirmText: 'Confirmar'
    });
    // Estado para el modal de resultados de ejecución inmediata
    const [modalResultado, setModalResultado] = useState({
        show: false,
        data: null
    });
    const [mesesVencimiento, setMesesVencimiento] = useState(3);
    const [tipoLicencia, setTipoLicencia] = useState('Todos');
    const [ordenDias, setOrdenDias] = useState('DIAS_DESC');
    const [pagina, setPagina] = useState(1);
    const [tamanioPagina, setTamanioPagina] = useState(15);

    const [estatusBorrado, setEstatusBorrado] = useState(0); // 0: Pendientes, 1: Programadas, 2: Ya Borradas

    // Filtrado y paginación en cliente (declarados arriba para que toggleSeleccionarTodo y todasSeleccionadasPaginaActual puedan acceder a ellos)
    const itemsFiltrados = useMemo(() => {
        if (!busqueda || !busqueda.trim()) return candidatasTotal;
        const query = busqueda.trim().toLowerCase();
        return candidatasTotal.filter(d => {
            const nombre = (d.vNombreEmpresa || d.NombreEmpresa || '').toLowerCase();
            const uuid = (d.vUUIDEmpresa || d.UUIDEmpresa || '').toLowerCase();
            const idLic = String(d.idLicencia || d.IdProgramacion || '');
            const server = (d.vServer || d.Servidor || '').toLowerCase();
            const bd = (d.vDatabase || d.BaseDatos || '').toLowerCase();
            return nombre.includes(query) || uuid.includes(query) || idLic.includes(query) || server.includes(query) || bd.includes(query);
        });
    }, [candidatasTotal, busqueda]);

    const totalRegistros = itemsFiltrados.length;
    const totalPaginas = Math.ceil(totalRegistros / tamanioPagina) || 1;

    const candidatasPaginadas = useMemo(() => {
        const inicio = (pagina - 1) * tamanioPagina;
        return itemsFiltrados.slice(inicio, inicio + tamanioPagina);
    }, [itemsFiltrados, pagina, tamanioPagina]);

    const { token, idCatTipoUsuario } = useToken();
    const isAdmin = idCatTipoUsuario === 1 || idCatTipoUsuario === '1';
    const { showErrorToast, showSuccessToast } = useToast();

    const copiarAlPortapapeles = (texto) => {
        if (!texto || texto === 'N/A') return;
        navigator.clipboard.writeText(texto)
            .then(() => {
                showSuccessToast('UUID copiado al portapapeles.');
            })
            .catch(() => {
                showErrorToast('Error al copiar el UUID.');
            });
    };

    // Helper para verificar si bTieneAmbas es verdadero
    const esTieneAmbas = (row) => {
        if (!row) return false;
        return row.bTieneAmbas === true ||
            row.bTieneAmbas === 1 ||
            String(row.bTieneAmbas) === '1' ||
            String(row.bTieneAmbas).toLowerCase() === 'true';
    };

    const solicitarConfirmacion = (title, message, variant, confirmText, onConfirm) => {
        setModalConfirm({
            show: true,
            title,
            message,
            onConfirm,
            variant,
            confirmText
        });
    };

    const cerrarModalConfirmacion = () => {
        setModalConfirm(prev => ({ ...prev, show: false }));
    };

    const toggleSeleccion = (row) => {
        const existe = licenciasSeleccionadas.find(l => l.idLicencia === row.idLicencia);
        if (existe) {
            setLicenciasSeleccionadas(licenciasSeleccionadas.filter(l => l.idLicencia !== row.idLicencia));
        } else {
            setLicenciasSeleccionadas([...licenciasSeleccionadas, row]);
        }
    };

    const toggleSeleccionarTodo = (e) => {
        if (e.target.checked) {
            const nuevasSeleccionadas = [...licenciasSeleccionadas];
            candidatasPaginadas.forEach(row => {
                if (!nuevasSeleccionadas.find(l => l.idLicencia === row.idLicencia)) {
                    nuevasSeleccionadas.push(row);
                }
            });
            setLicenciasSeleccionadas(nuevasSeleccionadas);
        } else {
            const currentIds = candidatasPaginadas.map(row => row.idLicencia);
            setLicenciasSeleccionadas(licenciasSeleccionadas.filter(l => !currentIds.includes(l.idLicencia)));
        }
    };

    const todasSeleccionadasPaginaActual = candidatasPaginadas.length > 0 && candidatasPaginadas.every(row => licenciasSeleccionadas.find(l => l.idLicencia === row.idLicencia));

    const handleEliminarSeleccionadas = () => {
        if (!isAdmin) {
            showErrorToast('Solo los administradores pueden programar el borrado de empresas.');
            return;
        }
        if (licenciasSeleccionadas.length === 0) return;
        solicitarConfirmacion(
            'Confirmar Borrado',
            `¿Estás seguro de que deseas programar el borrado de las ${licenciasSeleccionadas.length} licencias seleccionadas? Esta acción las encolará para ser borradas permanentemente por el servicio de mantenimiento nocturno.`,
            'danger',
            'Programar Borrado',
            async () => {
                setCargando(true);
                try {
                    const payload = {
                        licencias: licenciasSeleccionadas.map(l => ({
                            idLicencia: l.idLicencia,
                            vServer: l.vServer || '',
                            vDatabase: l.vDatabase || '',
                            vUUIDEmpresa: l.vUUIDEmpresa || '',
                            vTipoLicencia: l.vTipoLicencia || ''
                        }))
                    };

                    const response = await fetch('/api/Mantenimiento/EliminarDataLicenciasVencidas', {
                        method: 'POST',
                        headers: {
                            'Authorization': 'Bearer ' + token,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(payload)
                    });

                    if (response.ok) {
                        showSuccessToast('Empresas programadas para borrado correctamente.');
                        setLicenciasSeleccionadas([]);
                        cargarDatosServidor(mesesVencimiento, pagina, tamanioPagina, tipoLicencia, ordenDias);
                    } else {
                        showErrorToast(`Error al programar el borrado: ${response.statusText}`);
                    }
                } catch (error) {
                    showErrorToast(`Error: ${error.message}`);
                } finally {
                    setCargando(false);
                    cerrarModalConfirmacion();
                }
            }
        );
    };

    const handleQuitarBorrado = () => {
        if (!isAdmin) {
            showErrorToast('Solo los administradores pueden quitar empresas del borrado.');
            return;
        }
        if (licenciasSeleccionadas.length === 0) return;
        solicitarConfirmacion(
            'Quitar del Borrado',
            `¿Estás seguro de que deseas quitar del borrado a las ${licenciasSeleccionadas.length} empresas seleccionadas?`,
            'warning',
            'Quitar del Borrado',
            async () => {
                setCargando(true);
                try {
                    const payload = {
                        licencias: licenciasSeleccionadas.map(l => ({
                            idLicencia: l.idLicencia,
                            vServer: l.vServer || '',
                            vDatabase: l.vDatabase || '',
                            vUUIDEmpresa: l.vUUIDEmpresa || '',
                            vTipoLicencia: l.vTipoLicencia || '',
                            bIsActive: false
                        }))
                    };

                    const response = await fetch('/api/Mantenimiento/EliminarDataLicenciasVencidas', {
                        method: 'POST',
                        headers: {
                            'Authorization': 'Bearer ' + token,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(payload)
                    });

                    if (response.ok) {
                        showSuccessToast('Empresas quitadas del borrado correctamente.');
                        setLicenciasSeleccionadas([]);
                        cargarDatosServidor(mesesVencimiento, pagina, tamanioPagina, tipoLicencia, ordenDias, estatusBorrado);
                    } else {
                        showErrorToast(`Error al quitar del borrado: ${response.statusText}`);
                    }
                } catch (error) {
                    showErrorToast(`Error: ${error.message}`);
                } finally {
                    setCargando(false);
                    cerrarModalConfirmacion();
                }
            }
        );
    };



    const handleEjecutarBorradoInmediato = () => {
        if (!isAdmin) {
            showErrorToast('Solo los administradores pueden ejecutar el servicio de borrado.');
            return;
        }
        solicitarConfirmacion(
            'Ejecutar Borrado Inmediato',
            '¿Estás seguro de que deseas ejecutar el servicio de borrado de bases de datos ahora mismo? Antes de borrar, se validará individualmente cada empresa para garantizar que ninguna con licencias vigentes de Nube o AkasiaPOS sea eliminada.',
            'danger',
            'Ejecutar Inmediatamente',
            async () => {
                setCargando(true);
                try {
                    const response = await fetch('/api/Mantenimiento/EjecutarBorradoInmediato', {
                        method: 'POST',
                        headers: {
                            'Authorization': 'Bearer ' + token,
                            'Content-Type': 'application/json'
                        }
                    });

                    if (response.ok) {
                        const data = await response.json();
                        setModalResultado({ show: true, data });
                        showSuccessToast(`Servicio ejecutado: ${data.empresasExitosas || 0} exitosas, ${data.empresasFallidas || 0} omitidas/fallidas.`);
                        cargarDatosServidor(mesesVencimiento, pagina, tamanioPagina, tipoLicencia, ordenDias, estatusBorrado);
                    } else {
                        const errText = await response.text();
                        showErrorToast(`Error al ejecutar servicio: ${errText || response.statusText}`);
                    }
                } catch (error) {
                    showErrorToast(`Error de conexión: ${error.message}`);
                } finally {
                    setCargando(false);
                    cerrarModalConfirmacion();
                }
            }
        );
    };

    const handleExportarExcel = async () => {
        setCargando(true);
        try {
            let datosAExportar = [];

            if (licenciasSeleccionadas.length > 0) {
                // Si hay licencias seleccionadas explícitamente por el usuario
                datosAExportar = licenciasSeleccionadas;
            } else if (itemsFiltrados.length > 0) {
                // Datos filtrados o completos en pantalla
                datosAExportar = itemsFiltrados;
            } else {
                datosAExportar = candidatasTotal;
            }

            if (!datosAExportar || datosAExportar.length === 0) {
                showErrorToast('No hay datos disponibles para exportar.');
                setCargando(false);
                return;
            }

            // Generar tabla HTML para Excel con estilos
            let htmlContent = `
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8" />
<style>
    table { border-collapse: collapse; font-family: Arial, sans-serif; }
    th { background-color: #2c3e50; color: #ffffff; font-weight: bold; border: 1px solid #bdc3c7; text-align: center; padding: 8px; font-size: 14px; }
    td { border: 1px solid #bdc3c7; padding: 8px; font-size: 13px; text-align: left; }
    .text-center { text-align: center; }
    .row-even { background-color: #ecf0f1; }
    .row-odd { background-color: #ffffff; }
    .badge-nube { color: #2980b9; font-weight: bold; }
    .badge-pos { color: #8e44ad; font-weight: bold; }
    .danger-text { color: #c0392b; font-weight: bold; }
</style>
</head>
<body>
    <table>
        <thead>
            <tr>
                <th>UUID Empresa</th>
                <th>Nombre Empresa</th>
                <th>Tipo Licencia</th>
                <th>Días Vencidos</th>
                <th>Fecha Vencimiento</th>
                <th>Servidor</th>
                <th>Base de Datos</th>
                <th>${estatusBorrado === 1 ? 'Fecha en que se agregó' : 'Últ. Conexión Nube'}</th>
                ${estatusBorrado === 1 ? '' : '<th>Últ. Conexión AkasiaPOS</th>'}
            </tr>
        </thead>
        <tbody>`;

            datosAExportar.forEach((row, index) => {
                const bgClass = index % 2 === 0 ? 'row-even' : 'row-odd';
                const tipoClass = row.vTipoLicencia === 'Nube' ? 'badge-nube' : (row.vTipoLicencia === 'AkasiaPOS' ? 'badge-pos' : '');

                const fechaVencimiento = formatearFechaLarga(row.dFechaVencimiento);
                const fechaExtra = estatusBorrado === 1
                    ? formatearFechaLarga(row.dFechaProgramacion)
                    : formatearFechaLarga(row.dUltimaConexionNube);
                const fechaAkasiaPOS = formatearFechaLarga(row.dUltimaConexionAkasiaPOS);

                htmlContent += `
            <tr class="${bgClass}">
                <td style="mso-number-format:'@';">${row.vUUIDEmpresa || ''}</td>
                <td>${(row.vNombreEmpresa || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>
                <td class="${tipoClass} text-center">${row.vTipoLicencia || ''}</td>
                <td class="text-center danger-text">${row.iDiasVencidos || 0}</td>
                <td class="text-center">${fechaVencimiento}</td>
                <td>${row.vServer || ''}</td>
                <td>${row.vDatabase || ''}</td>
                <td class="text-center">${fechaExtra}</td>
                ${estatusBorrado === 1 ? '' : `<td class="text-center">${fechaAkasiaPOS}</td>`}
            </tr>`;
            });

            htmlContent += `
        </tbody>
    </table>
</body>
</html>`;

            // Crear y descargar archivo
            const blob = new Blob([htmlContent], { type: 'application/vnd.ms-excel;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.setAttribute('download', `Empresas_${estatusBorrado === 1 ? 'Programadas' : 'Candidatas'}_Borrado_${new Date().getTime()}.xls`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            showSuccessToast('Archivo exportado a Excel correctamente.');
        } catch (error) {
            showErrorToast(`Error: ${error.message}`);
        } finally {
            setCargando(false);
        }
    };

    // Helper para ordenar por días vencidos
    const aplicarOrdenamiento = (listado, orden) => {
        if (!listado || listado.length === 0) return [];
        const copia = [...listado];
        if (orden === 'DIAS_ASC') {
            copia.sort((a, b) => (a.iDiasVencidos || 0) - (b.iDiasVencidos || 0));
        } else {
            copia.sort((a, b) => (b.iDiasVencidos || 0) - (a.iDiasVencidos || 0));
        }
        return copia;
    };

    // Cargar datos desde la API
    const cargarDatosServidor = async (meses, pag, tam, tipo, orden, estatus = null) => {
        setCargando(true);
        const valTipo = tipo !== undefined ? tipo : tipoLicencia;
        const valOrden = orden !== undefined ? orden : ordenDias;
        const valEstatus = estatus !== null ? estatus : estatusBorrado;

        setLicenciasSeleccionadas([]);

        try {
            if (valEstatus >= 1) {
                // Modo: 
                // valEstatus = 1 (Ya Programadas) -> iEstatusBorrado = 0 (en espera)
                // valEstatus = 2 (No Borradas / Omitidas) -> iEstatusBorrado = 2
                // valEstatus = 3 (Borradas Exitosamente) -> iEstatusBorrado = 1
                let iEstatusParam = 0;
                if (valEstatus === 2) iEstatusParam = 2;
                else if (valEstatus === 3) iEstatusParam = 1;

                const response = await fetch(`/api/Mantenimiento/ObtenerEmpresasPendientesBorrado?iEstatusBorrado=${iEstatusParam}`, {
                    method: 'GET',
                    headers: { 'Authorization': 'Bearer ' + token }
                });

                if (response.ok) {
                    const data = await response.json();
                    const dataTable = data.table3 || [];
                    const statsTable = data.table2 || [];

                    let listadoRaw = Array.isArray(dataTable) ? dataTable : [];

                    // Mapear al modelo de la tabla principal
                    let mappedData = listadoRaw.map(d => {
                        let tipoLic = 'Desconocido';
                        if (d.TipoLicencia) {
                            tipoLic = d.TipoLicencia;
                        } else if (d.idCatTipoLicencia !== undefined && d.idCatTipoLicencia !== null) {
                            const idTipo = String(d.idCatTipoLicencia);
                            tipoLic = idTipo === '3' ? 'Nube' : (idTipo === '2' ? 'AkasiaPOS' : 'Desconocido');
                        }

                        const fechaProg = d.FechaProgramacion || d.FechaAgregada || d.FechaAgregadam || null;

                        return {
                            idLicencia: d.idLicencia || d.IdProgramacion || d.idProgramacion || 0,
                            idProgramacion: d.IdProgramacion || d.idProgramacion || 0,
                            vNombreEmpresa: d.NombreEmpresa || d.nombreEmpresa || 'Sin Nombre',
                            vUUIDEmpresa: d.UUIDEmpresa || d.uuidEmpresa,
                            vTipoLicencia: tipoLic,
                            iDiasVencidos: d.DiasVencidos !== undefined ? d.DiasVencidos : (d.diasVencidos || 0),
                            vServer: d.Servidor || d.servidor,
                            vDatabase: d.BaseDatos || d.baseDatos,
                            dFechaVencimiento: d.FechaVencimiento || d.fechaVencimiento,
                            dFechaProgramacion: fechaProg,
                            dFechaBorrado: d.FechaBorrado || d.fechaBorrado || null,
                            bTieneLicenciaVigente: d.bTieneLicenciaVigente === true || d.bTieneLicenciaVigente === 1 || String(d.bTieneLicenciaVigente) === '1',
                            vServerVigente: d.vServerVigente || d.serverVigente || null,
                            vDatabaseVigente: d.vDatabaseVigente || d.databaseVigente || null,
                            vTipoLicenciaVigente: d.vTipoLicenciaVigente || d.tipoLicenciaVigente || null,
                            dFinLicenciaVigente: d.dFinLicenciaVigente || d.fechaVencimientoVigente || null,
                            vMotivo: d.Motivo || d.motivo || null,
                            bYaProgramada: true
                        };
                    });

                    // Filtrar por Tipo de Licencia
                    if (valTipo === 'Nube') mappedData = mappedData.filter(d => d.vTipoLicencia === 'Nube');
                    else if (valTipo === 'AkasiaPOS') mappedData = mappedData.filter(d => d.vTipoLicencia === 'AkasiaPOS');
                    else if (valTipo === 'Ambas') mappedData = [];

                    // Ordenar local
                    mappedData = aplicarOrdenamiento(mappedData, valOrden);

                    setCandidatasTotal(mappedData);

                    // Actualizar Resumen (usando las estadísticas reales del SP)
                    if (statsTable && Array.isArray(statsTable) && statsTable.length > 0) {
                        const res = statsTable[0];
                        setResumen(res);
                        setConteoTabs(prev => ({
                            ...prev,
                            0: prev[0] || (res.iTotalPendientes ?? 0),
                            1: res.iTotalProgramadas ?? (valEstatus === 1 ? mappedData.length : prev[1]),
                            2: res.iTotalNoBorradas ?? (valEstatus === 2 ? mappedData.length : prev[2]),
                            3: res.iTotalBorradas ?? (valEstatus === 3 ? mappedData.length : prev[3]),
                            [valEstatus]: mappedData.length
                        }));
                    }
                } else {
                    showErrorToast(`Error: ${response.statusText}`);
                }
            } else {
                // Modo: Pendientes (Default)
                const url = `/api/Mantenimiento/ObtenerLicenciasVencidasDataBorrado?iMesesVencimiento=${meses}&vTipoLicencia=${valTipo}&vOrden=${valOrden}&pagina=1&tamanioPagina=999999`;
                const response = await fetch(url, {
                    method: 'GET',
                    headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' }
                });

                if (response.ok) {
                    const data = await response.json();
                    let detalles = data;
                    if (data.detalles) {
                        try { detalles = typeof data.detalles === 'string' ? JSON.parse(data.detalles) : data.detalles; }
                        catch (e) { console.error(e); }
                    }

                    const t2 = detalles.table2 || data.table2;
                    const t3 = detalles.table3 || data.table3;
                    const listadoRaw = t3 || (t2 && (t2.length === 0 || t2[0].iTotalCandidatas === undefined) ? t2 : []) || [];

                    let listadoConVigentes = listadoRaw.map(d => ({
                        ...d,
                        bTieneLicenciaVigente: d.bTieneLicenciaVigente === true || d.bTieneLicenciaVigente === 1 || String(d.bTieneLicenciaVigente) === '1',
                        vServerVigente: d.vServerVigente || d.serverVigente || null,
                        vDatabaseVigente: d.vDatabaseVigente || d.databaseVigente || null,
                        vTipoLicenciaVigente: d.vTipoLicenciaVigente || d.tipoLicenciaVigente || null,
                        dFinLicenciaVigente: d.dFinLicenciaVigente || d.fechaVencimientoVigente || null
                    }));

                    const listadoOrdenado = aplicarOrdenamiento(listadoConVigentes, valOrden);
                    setCandidatasTotal(listadoOrdenado);

                    const resumenRow = (t2 && t2.length > 0 && t2[0].iTotalCandidatas !== undefined) ? t2[0] : null;
                    if (resumenRow) {
                        setResumen(resumenRow);
                        setConteoTabs(prev => ({
                            ...prev,
                            0: listadoOrdenado.length || resumenRow.iTotalCandidatas || 0,
                            1: resumenRow.iTotalProgramadas ?? prev[1],
                            2: resumenRow.iTotalNoBorradas ?? prev[2],
                            3: resumenRow.iTotalBorradas ?? prev[3]
                        }));
                    }
                } else {
                    showErrorToast(`${response.status} ${response.statusText}`);
                }
            }
        } catch (error) {
            showErrorToast('Error al conectar con el servidor: ' + error);
        } finally {
            setCargando(false);
        }
    };

    // 1. Cargar UNA SOLA VEZ al abrir la pantalla con orden por defecto DIAS_DESC
    useEffect(() => {
        cargarDatosServidor(3, 1, 15, 'Todos', 'DIAS_DESC');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handlers por eventos
    const handleCambiarMeses = (e) => {
        const nuevosMeses = Number(e.target.value);
        setMesesVencimiento(nuevosMeses);
        setPagina(1);
        cargarDatosServidor(nuevosMeses, 1, tamanioPagina, tipoLicencia, ordenDias, estatusBorrado);
    };

    const handleCambiarTipoLicencia = (e) => {
        const nuevoTipo = e.target.value;
        setTipoLicencia(nuevoTipo);
        setPagina(1);
        cargarDatosServidor(mesesVencimiento, 1, tamanioPagina, nuevoTipo, ordenDias, estatusBorrado);
    };

    const handleCambiarEstatusBorrado = (estatus) => {
        setEstatusBorrado(estatus);
        setBusqueda('');
        setPagina(1);
        cargarDatosServidor(mesesVencimiento, 1, tamanioPagina, tipoLicencia, ordenDias, estatus);
    };

    const handleCambiarBusqueda = (e) => {
        setBusqueda(e.target.value);
        setPagina(1);
    };

    const handleLimpiarBusqueda = () => {
        setBusqueda('');
        setPagina(1);
    };

    const toggleOrdenColumna = () => {
        const siguienteOrden = ordenDias === 'DIAS_DESC' ? 'DIAS_ASC' : 'DIAS_DESC';
        setOrdenDias(siguienteOrden);
        setCandidatasTotal(prev => aplicarOrdenamiento(prev, siguienteOrden));
    };

    const handlePaginaAnterior = () => {
        setPagina(prev => Math.max(1, prev - 1));
    };

    const handlePaginaSiguiente = () => {
        setPagina(prev => Math.min(totalPaginas, prev + 1));
    };

    const handleCambiarTamanio = (e) => {
        const nuevoTam = Number(e.target.value);
        setTamanioPagina(nuevoTam);
        setPagina(1);
    };

    const formatearFechaLarga = (fechaStr) => {
        if (!fechaStr) return 'N/A';
        const d = new Date(fechaStr);
        if (isNaN(d.getTime())) return fechaStr;
        return d.toLocaleDateString('es-MX', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const formatearFechaCorta = (fechaStr) => {
        if (!fechaStr) return 'Sin registro';
        const d = new Date(fechaStr);
        if (isNaN(d.getTime())) return fechaStr;
        return d.toLocaleDateString('es-MX', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <>
            {/* Encabezado y Filtros según Mockup */}
            <HeaderFiltrosBorrado
                estatusBorrado={estatusBorrado}
                handleCambiarEstatusBorrado={handleCambiarEstatusBorrado}
                tipoLicencia={tipoLicencia}
                handleCambiarTipoLicencia={handleCambiarTipoLicencia}
                mesesVencimiento={mesesVencimiento}
                handleCambiarMeses={handleCambiarMeses}
                busqueda={busqueda}
                handleCambiarBusqueda={handleCambiarBusqueda}
                handleLimpiarBusqueda={handleLimpiarBusqueda}
                conteoTabs={conteoTabs}
                cargando={cargando}
            >
                {isAdmin && licenciasSeleccionadas.length > 0 && estatusBorrado === 0 && (
                    <Button
                        variant="danger"
                        size="sm"
                        className="d-flex align-items-center gap-2 px-3 py-2 fw-semi-bold text-white shadow-none"
                        style={{ borderRadius: '8px', height: '38px' }}
                        onClick={handleEliminarSeleccionadas}
                        disabled={cargando}
                    >
                        <i className="far fa-clock"></i>
                        <span>Programar Borrado ({licenciasSeleccionadas.length})</span>
                    </Button>
                )}
                {isAdmin && licenciasSeleccionadas.length > 0 && estatusBorrado === 1 && (
                    <Button
                        variant="warning"
                        size="sm"
                        className="d-flex align-items-center gap-2 px-3 py-2 fw-semi-bold text-white shadow-none"
                        style={{ borderRadius: '8px', height: '38px' }}
                        onClick={handleQuitarBorrado}
                        disabled={cargando}
                    >
                        <i className="fas fa-undo"></i>
                        <span>Quitar del Borrado ({licenciasSeleccionadas.length})</span>
                    </Button>
                )}
                <Button
                    variant="outline-success"
                    size="sm"
                    className="d-flex align-items-center gap-2 px-3 py-2 fw-semi-bold text-nowrap shadow-none"
                    style={{ borderRadius: '8px', height: '38px' }}
                    onClick={handleExportarExcel}
                    disabled={cargando}
                    title="Exportar a Excel"
                >
                    <i className="far fa-file-excel fs-0"></i>
                    <span>Exportar a Excel</span>
                </Button>
                {isAdmin && (
                    <Button
                        variant="danger"
                        size="sm"
                        className="d-flex align-items-center gap-2 px-3 py-2 fw-semi-bold text-white text-nowrap shadow-none"
                        style={{ backgroundColor: '#e63757', borderColor: '#e63757', borderRadius: '8px', height: '38px' }}
                        onClick={handleEjecutarBorradoInmediato}
                        disabled={cargando}
                        title="Ejecutar el servicio de borrado inmediatamente"
                    >
                        <i className="far fa-trash-alt fs-0"></i>
                        <span>Ejecutar Borrado Inmediato</span>
                    </Button>
                )}
            </HeaderFiltrosBorrado>

            {/* Tarjetas de Resumen KPI */}
            <TarjetasResumenKPI resumen={resumen} estatusBorrado={estatusBorrado} />

            {/* Tabla Principal */}
            <Card>
                <Card.Body className="p-0">
                    {estatusBorrado === 2 || estatusBorrado === 3 ? (
                        <TablaEmpresasYaBorradas
                            candidatasPaginadas={candidatasPaginadas}
                            cargando={cargando}
                            formatearFechaLarga={formatearFechaLarga}
                            formatearFechaCorta={formatearFechaCorta}
                            copiarAlPortapapeles={copiarAlPortapapeles}
                            estatusBorrado={estatusBorrado}
                        />
                    ) : estatusBorrado === 1 ? (
                        <TablaEmpresasYaProgramadas
                            candidatasPaginadas={candidatasPaginadas}
                            licenciasSeleccionadas={licenciasSeleccionadas}
                            todasSeleccionadasPaginaActual={todasSeleccionadasPaginaActual}
                            toggleSeleccionarTodo={toggleSeleccionarTodo}
                            toggleSeleccion={toggleSeleccion}
                            cargando={cargando}
                            formatearFechaLarga={formatearFechaLarga}
                            formatearFechaCorta={formatearFechaCorta}
                            copiarAlPortapapeles={copiarAlPortapapeles}
                            toggleOrdenColumna={toggleOrdenColumna}
                            ordenDias={ordenDias}
                            isAdmin={isAdmin}
                        />
                    ) : (
                        <TablaLicenciasPendientes
                            candidatasPaginadas={candidatasPaginadas}
                            licenciasSeleccionadas={licenciasSeleccionadas}
                            todasSeleccionadasPaginaActual={todasSeleccionadasPaginaActual}
                            toggleSeleccionarTodo={toggleSeleccionarTodo}
                            toggleSeleccion={toggleSeleccion}
                            cargando={cargando}
                            toggleOrdenColumna={toggleOrdenColumna}
                            ordenDias={ordenDias}
                            esTieneAmbas={esTieneAmbas}
                            formatearFechaLarga={formatearFechaLarga}
                            formatearFechaCorta={formatearFechaCorta}
                            copiarAlPortapapeles={copiarAlPortapapeles}
                            isAdmin={isAdmin}
                        />
                    )}
                </Card.Body>

                {/* Footer  */}
                <Card.Footer className="bg-white py-2">
                    <Row className="align-items-center justify-content-between g-2">
                        <Col xs="auto">
                            <span className="fs--1 text-600">
                                Mostrando {totalRegistros > 0 ? ((pagina - 1) * tamanioPagina) + 1 : 0} a {Math.min(pagina * tamanioPagina, totalRegistros)} de <strong>{totalRegistros}</strong> licencias {busqueda ? 'encontradas' : 'candidatas'}
                            </span>
                        </Col>
                        <Col xs="auto" className="d-flex align-items-center gap-2">
                            <Form.Select
                                size="sm"
                                style={{ width: 'auto' }}
                                value={tamanioPagina}
                                onChange={handleCambiarTamanio}
                            >
                                <option value={10}>10 por pág.</option>
                                <option value={15}>15 por pág.</option>
                                <option value={25}>25 por pág.</option>
                                <option value={50}>50 por pág.</option>
                                <option value={100}>100 por pág.</option>
                            </Form.Select>

                            <div className="d-flex align-items-center gap-1">
                                <Button
                                    size="sm"
                                    variant="falcon-default"
                                    disabled={pagina <= 1 || cargando}
                                    onClick={handlePaginaAnterior}
                                >
                                    Anterior
                                </Button>
                                <span className="fs--1 px-2 text-700">
                                    Pág. {pagina} de {totalPaginas}
                                </span>
                                <Button
                                    size="sm"
                                    variant="falcon-default"
                                    disabled={pagina >= totalPaginas || cargando}
                                    onClick={handlePaginaSiguiente}
                                >
                                    Siguiente
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Card.Footer>
            </Card>

            {/* Modal de Confirmación Genérico */}
            <Modal show={modalConfirm.show} onHide={cerrarModalConfirmacion} centered>
                <Modal.Header closeButton className={`bg-${modalConfirm.variant}`}>
                    <Modal.Title className="text-white h5 mb-0">{modalConfirm.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="py-4">
                    <p className="mb-0 fs--1">{modalConfirm.message}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={cerrarModalConfirmacion} disabled={cargando}>
                        Cancelar
                    </Button>
                    <Button variant={modalConfirm.variant} onClick={modalConfirm.onConfirm} disabled={cargando}>
                        {cargando ? <Spinner size="sm" animation="border" /> : modalConfirm.confirmText}
                    </Button>
                </Modal.Footer>
            </Modal>
            {/* Modal de Resultados de Ejecución Inmediata */}
            <Modal show={modalResultado.show} onHide={() => setModalResultado({ show: false, data: null })} size="lg" centered>
                <Modal.Header closeButton className="bg-primary">
                    <Modal.Title className="text-white h5 mb-0">
                        <i className="fas fa-clipboard-check me-2"></i>Resultado del Servicio de Borrado
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="py-3">
                    {modalResultado.data && (
                        <>
                            <Row className="g-2 mb-3">
                                <Col xs={4}>
                                    <div className="p-2 border rounded text-center bg-white">
                                        <div className="text-600 fs--2">Procesadas</div>
                                        <div className="fs-1 fw-bold text-700">{modalResultado.data.totalEmpresasProcesadas || 0}</div>
                                    </div>
                                </Col>
                                <Col xs={4}>
                                    <div className="p-2 border rounded text-center bg-white">
                                        <div className="text-600 fs--2">Exitosas</div>
                                        <div className="fs-1 fw-bold text-success">{modalResultado.data.empresasExitosas || 0}</div>
                                    </div>
                                </Col>
                                <Col xs={4}>
                                    <div className="p-2 border rounded text-center bg-white">
                                        <div className="text-600 fs--2">Omitidas / Fallidas</div>
                                        <div className="fs-1 fw-bold text-danger">{modalResultado.data.empresasFallidas || 0}</div>
                                    </div>
                                </Col>
                            </Row>
                            <div className="table-responsive" style={{ maxHeight: '300px' }}>
                                <Table size="sm" striped hover className="fs--2 mb-0">
                                    <thead className="bg-200">
                                        <tr>
                                            <th>Empresa (UUID)</th>
                                            <th>Servidor / BD</th>
                                            <th>Estatus</th>
                                            <th>Motivo / BD Vigente</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(modalResultado.data.detalles || []).map((det, i) => (
                                            <tr key={i}>
                                                <td>
                                                    <div className="font-monospace text-truncate" style={{ maxWidth: '140px' }}>{det.uuidEmpresa}</div>
                                                    <div className="text-muted">Licencia: {det.idLicencia}</div>
                                                </td>
                                                <td>
                                                    <div>{det.servidor}</div>
                                                    <div className="text-muted">{det.baseDatos}</div>
                                                </td>
                                                <td>
                                                    <Badge bg={det.exitoso ? 'soft-success' : 'soft-danger'} className={det.exitoso ? 'text-success' : 'text-danger'}>
                                                        {det.exitoso ? 'Borrado' : 'No Borrado'}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <div className="text-800">{det.motivo || '-'}</div>
                                                    {det.servidorVigente && (
                                                        <div className="text-warning fw-semi-bold">
                                                            Vigente en: {det.servidorVigente} / {det.baseDatosVigente}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" size="sm" onClick={() => setModalResultado({ show: false, data: null })}>
                        Entendido
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default LicenciasVencidasDataBorrado;
